import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
});

export const getMeta = () => api.get("/meta").then((r) => r.data);
export const prescreenCase = (payload) => api.post("/cases/prescreen", payload).then((r) => r.data);
export const createCase = (payload) => api.post("/cases", payload).then((r) => r.data);
export const getCase = (id) => api.get(`/cases/${id}`).then((r) => r.data);
export const listCases = (email) =>
    api.get("/cases", { params: email ? { user_email: email } : {} }).then((r) => r.data);
export const searchLaw = (q, opts = {}) =>
    api.get("/search", { params: { q, ...opts } }).then((r) => r.data);

// Attorney matching
export const listAttorneys = (params = {}) =>
    api.get("/attorneys", { params }).then((r) => r.data);
export const getAttorney = (id) => api.get(`/attorneys/${id}`).then((r) => r.data);
export const getCaseMatches = (caseId, topN = 5) =>
    api.get(`/cases/${caseId}/matches`, { params: { top_n: topN } }).then((r) => r.data);

// Referrals
export const createReferral = (payload) =>
    api.post("/referrals", payload).then((r) => r.data);
export const listReferrals = (params = {}) =>
    api.get("/referrals", { params }).then((r) => r.data);
export const updateReferralStatus = (id, status) =>
    api.patch(`/referrals/${id}/status`, null, { params: { status } }).then((r) => r.data);

// Law firms
export const getFirmTiers = () => api.get("/firms/tiers").then((r) => r.data);
export const registerFirm = (payload) =>
    api.post("/firms/register", payload).then((r) => r.data);
export const loginFirm = (contact_email) =>
    api.post("/firms/login", { contact_email }).then((r) => r.data);
export const getFirm = (firmId) => api.get(`/firms/${firmId}`).then((r) => r.data);
export const getFirmLeads = (firmId, limit = 25) =>
    api.get(`/firms/${firmId}/leads`, { params: { limit } }).then((r) => r.data);
export const claimLead = (firmId, caseId) =>
    api.post(`/firms/${firmId}/claim/${caseId}`).then((r) => r.data);
export const getFirmDashboard = (firmId) =>
    api.get(`/firms/${firmId}/dashboard`).then((r) => r.data);

// Payments
export const createCheckout = (payload) =>
    api.post("/payments/checkout", payload).then((r) => r.data);
export const getPaymentStatus = (sessionId) =>
    api.get(`/payments/status/${sessionId}`).then((r) => r.data);

// True recurring subscriptions (auto-renew + customer portal)
export const subscribeFirm = (payload) =>
    api.post("/firms/subscribe", payload).then((r) => r.data);
export const getFirmStats = () =>
    api.get("/firms/stats").then((r) => r.data);
export const getFirmSubscription = (firmId) =>
    api.get(`/firms/${firmId}/subscription`).then((r) => r.data);
export const openBillingPortal = (firmId, originUrl) =>
    api
        .post("/firms/portal", null, {
            params: { firm_id: firmId, origin_url: originUrl },
        })
        .then((r) => r.data);
export const cancelFirmSubscription = (firmId) =>
    api.post(`/firms/${firmId}/subscription/cancel`).then((r) => r.data);
export const resumeFirmSubscription = (firmId) =>
    api.post(`/firms/${firmId}/subscription/resume`).then((r) => r.data);

// Pay-per-lead
export const getLeadPrice = (caseId) =>
    api.get(`/leads/${caseId}/price`).then((r) => r.data);
export const purchaseLead = (payload) =>
    api.post("/leads/purchase", payload).then((r) => r.data);

// User premium products
export const listProducts = () => api.get("/products").then((r) => r.data);
export const purchaseProduct = (payload) =>
    api.post("/products/purchase", payload).then((r) => r.data);
export const getCaseDocuments = (caseId, userEmail) =>
    api
        .get(`/cases/${caseId}/documents`, {
            params: userEmail ? { user_email: userEmail } : {},
        })
        .then((r) => r.data);

// Showcase
export const listShowcase = (minScore = 80) =>
    api.get("/showcase", { params: { min_score: minScore } }).then((r) => r.data);
export const getShowcase = (caseId) =>
    api.get(`/showcase/${caseId}`).then((r) => r.data);

// Legal Education Blog
export const listBlog = (category, tag, lang) =>
    api
        .get("/blog", {
            params: {
                ...(category ? { category } : {}),
                ...(tag ? { tag } : {}),
                ...(lang && lang !== "en" ? { lang } : {}),
            },
        })
        .then((r) => r.data);
export const getBlog = (slug, lang) =>
    api
        .get(`/blog/${slug}`, {
            params: lang && lang !== "en" ? { lang } : {},
        })
        .then((r) => r.data);
export const getBlogTopics = () => api.get("/blog/topics").then((r) => r.data);
export const generateBlogPost = (topicKey) =>
    api.post("/blog/generate", { topic_key: topicKey }).then((r) => r.data);
export const translateBlogPost = (slug, lang) =>
    api.post(`/blog/${slug}/translate`, null, { params: { lang } }).then((r) => r.data);
export const getBlogSocial = (slug) =>
    api.get(`/blog/${slug}/social`).then((r) => r.data);

// Blog leads (reader → lead capture → firm marketplace)
export const submitBlogLead = (payload) =>
    api.post("/blog-leads", payload).then((r) => r.data);
export const getFirmBlogLeads = (firmId, limit = 50) =>
    api.get(`/blog-leads/firm/${firmId}`, { params: { limit } }).then((r) => r.data);
export const purchaseBlogLead = (payload) =>
    api.post("/blog-leads/purchase", payload).then((r) => r.data);
export const getBlogLeadFull = (leadId, firmId) =>
    api.get(`/blog-leads/${leadId}/full`, { params: { firm_id: firmId } }).then((r) => r.data);

// Newsletter
export const newsletterSubscribe = (payload) =>
    api.post("/newsletter/subscribe", payload).then((r) => r.data);
export const newsletterStats = () => api.get("/newsletter/stats").then((r) => r.data);

// Share tracking + leaderboard
export const trackShare = (slug, platform) =>
    api.post(`/blog/${slug}/share`, { platform }).then((r) => r.data).catch(() => null);
export const getBlogLeaderboard = (limit = 10) =>
    api.get("/blog-leaderboard", { params: { limit } }).then((r) => r.data);

// Lead view tracking (scarcity "X firms viewing")
export const trackLeadView = (leadId, firmId) =>
    api
        .post(`/blog-leads/${leadId}/view`, { firm_id: firmId })
        .then((r) => r.data)
        .catch(() => null);

// SEO landing pages
export const getSeoLanding = (state, practice) =>
    api.get(`/seo/lawyer/${state}/${practice}`).then((r) => r.data);
export const getSeoIndex = () => api.get("/seo/index").then((r) => r.data);

// Firms directory + leaderboard (public)
export const getFirmsDirectory = (filters = {}) =>
    api.get("/firms-directory", { params: filters }).then((r) => r.data);
export const getFirmsLeaderboard = (limit = 10) =>
    api.get("/firms-leaderboard", { params: { limit } }).then((r) => r.data);

// Spotlight sponsorships
export const getSpotlightSlots = () => api.get("/spotlight/slots").then((r) => r.data);
export const getSpotlightCurrent = (slot) =>
    api.get("/spotlight/current", { params: { slot } }).then((r) => r.data).catch(() => ({ sponsor: null }));
export const purchaseSpotlight = (payload) =>
    api.post("/spotlight/purchase", payload).then((r) => r.data);
export const getFirmSpotlights = (firmId) =>
    api.get(`/spotlight/firm/${firmId}`).then((r) => r.data);

// Public stats (live counter)
export const getPublicStats = () => api.get("/stats/public").then((r) => r.data);

export const HPM_LANG_KEY = "hpm_blog_lang";
export const getStoredLang = () => localStorage.getItem(HPM_LANG_KEY) || "en";
export const setStoredLang = (l) => localStorage.setItem(HPM_LANG_KEY, l);

// Marketplace conversion
export const markReferralConverted = (referralId, payload) =>
    api.post(`/referrals/${referralId}/converted`, payload).then((r) => r.data);
export const getFirmCommissions = (firmId) =>
    api.get(`/firms/${firmId}/commissions`).then((r) => r.data);

// Firm session storage
export const HPM_FIRM_KEY = "hpm_firm_session";
export const getFirmSession = () => {
    try {
        const raw = localStorage.getItem(HPM_FIRM_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (_) {
        return null;
    }
};
export const setFirmSession = (firm) =>
    localStorage.setItem(HPM_FIRM_KEY, JSON.stringify(firm));
export const clearFirmSession = () => localStorage.removeItem(HPM_FIRM_KEY);

export const HPM_EMAIL_KEY = "hpm_user_email";
export const getStoredEmail = () => localStorage.getItem(HPM_EMAIL_KEY) || "";
export const setStoredEmail = (e) => localStorage.setItem(HPM_EMAIL_KEY, e);
